/* eslint-disable no-unused-vars */
import React, {useEffect, useState, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {Modal} from 'react-bootstrap'

import {KTSVG} from '../../helpers'
import {Loading, KTCardBody, KTCardHeader} from '../../components'
import {
  subcontractorDocumentGetByGuid,
  subcontractorDocumentUploadFile,
  setUploaded,
} from '../../redux/slices/subcontractordocument.slice'

const FILE_ACCEPT =
  'image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const selectSubcontractorDocumentData = (state) => ({
  publicDetail: state.subcontractorDocument.publicDetail,
  isUploaded: state.subcontractorDocument.isUploaded,
  loading: state.subcontractorDocument.loading,
})

function SubcontractorDocumentUpload() {
  const {id} = useParams()
  const intl = useIntl()
  const dispatch = useDispatch()
  const {publicDetail, isUploaded, loading} = useSelector(selectSubcontractorDocumentData)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(subcontractorDocumentGetByGuid({SubcontractorDocumentGuid: id}))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (isUploaded) {
      setShow(true)
      dispatch(setUploaded(false))
      dispatch(subcontractorDocumentGetByGuid({SubcontractorDocumentGuid: id}))
    }
  }, [isUploaded, dispatch, id])

  const validSchema = Yup.object().shape({
    FormFile: Yup.mixed().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(subcontractorDocumentUploadFile(values))
      resetForm()
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: {
      SubcontractorDocumentGuid: id,
      FormFile: null,
      Description: '',
    },
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  return (
    <>
      {publicDetail && (
        <KTCardBody className='p-0 pt-4'>
          <div className='text-center mb-11'>
            <h1 className='text-dark fw-bolder mb-3'>{publicDetail.companyTitle}</h1>
            <div className='text-gray-500 fw-semibold fs-5'>
              {publicDetail.projectName} — {publicDetail.year} /{' '}
              {intl.formatDate(new Date(2000, (publicDetail.month || 1) - 1, 1), {month: 'long'})}
            </div>
            {publicDetail.description && (
              <div className='text-gray-600 fw-semibold fs-6 mt-4' style={{whiteSpace: 'pre-wrap'}}>
                <div className='fw-bold'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.REQUESTED'})}
                </div>
                {publicDetail.description}
              </div>
            )}
          </div>
        </KTCardBody>
      )}

      <KTCardHeader
        className='ps-0'
        title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.TITLE'})}
      />

      <KTCardBody className='p-0'>
        <form className='form' onSubmit={formik.handleSubmit} noValidate>
          <div className='row mt-11'>
            <div className='col-md-12'>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.FILE'})}
                </label>
                <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                  <label
                    htmlFor='FormFile'
                    className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden d-flex align-items-center justify-content-center'
                  >
                    <input
                      className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                      type='file'
                      accept={FILE_ACCEPT}
                      id='FormFile'
                      name='FormFile'
                      multiple
                      onChange={(event) =>
                        formik.setFieldValue('FormFile', event.currentTarget.files)
                      }
                    />
                    <KTSVG
                      path='/images/icons/duotune/communication/com008.svg'
                      className='svg-icon-3 position-relative'
                    />
                    {formik.values.FormFile && formik.values.FormFile.length > 0
                      ? Array.from(formik.values.FormFile)
                          .map((file) => file.name)
                          .join(', ')
                      : intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.CHOOSEFILE'})}
                  </label>
                  {formik.touched.FormFile && formik.errors.FormFile && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.FormFile}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.DESCRIPTION'})}
                </label>
                <div className='col-lg-9 fv-row fv-plugins-icon-container'>
                  <textarea
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    disabled={formik.isSubmitting}
                    rows={3}
                    placeholder={intl.formatMessage({
                      id: 'SUBCONTRACTORDOCUMENT.PUBLIC.DESCRIPTION.PLACEHOLDER',
                    })}
                    {...formik.getFieldProps('Description')}
                  />
                </div>
              </div>

              <div className='row pt-5'>
                <div className='col-lg-12 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.values.FormFile}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </KTCardBody>

      {publicDetail && publicDetail.files && publicDetail.files.length > 0 && (
        <>
          <KTCardHeader
            className='ps-0 mt-11'
            title={intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.UPLOADEDFILES'})}
          />
          <KTCardBody className='p-0'>
            <div className='table-responsive mh-400px overflow-auto'>
              <table className='table align-middle table-row-dashed fs-6 gy-3'>
                <thead>
                  <tr className='text-start fw-bolder fs-7 text-uppercase gs-0'>
                    <th>{intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.FILENAME'})}</th>
                    <th>
                      {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.FILEDESCRIPTION'})}
                    </th>
                    <th>{intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.UPLOADDATE'})}</th>
                    <th className='text-end'></th>
                  </tr>
                </thead>
                <tbody className='text-gray-600 fw-semibold'>
                  {publicDetail.files.map((file, index) => (
                    <tr key={index}>
                      <td>{file.name}</td>
                      <td>{file.description}</td>
                      <td>
                        {file.createdDate &&
                          intl.formatDate(new Date(file.createdDate), {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                          })}
                      </td>
                      <td className='text-end'>
                        {file.fileUrl && (
                          <a
                            href={file.fileUrl}
                            target='_blank'
                            rel='noreferrer'
                            className='btn btn-sm btn-light btn-color-muted btn-active-light-success px-4 py-2'
                          >
                            <KTSVG
                              path='/images/icons/duotune/files/fil021.svg'
                              className='svg-icon-3'
                            />
                            {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.DETAIL.DOWNLOAD'})}
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </KTCardBody>
        </>
      )}

      <Modal
        className='modal-sticky modal-sticky-lg modal-sticky-bottom-right'
        role='dialog'
        data-backdrop='false'
        aria-hidden='true'
        tabIndex='-1'
        show={show}
        animation={false}
        aria-labelledby='contained-modal-title-vcenter'
        centered
        onHide={() => setShow(false)}
      >
        <div className='modal-content'>
          <div className='d-flex align-items-center justify-content-between py-5 ps-8 pe-5 border-bottom'>
            <h5 className='fw-bold m-0'>
              {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.TITLE'})}
            </h5>
            <div
              className='btn btn-icon btn-sm btn-light-primary ms-2'
              data-bs-dismiss='modal'
              onClick={() => setShow(false)}
            >
              <KTSVG className='svg-icon-1' path='/images/icons/duotune/arrows/arr061.svg' />
            </div>
          </div>

          <div className='d-block text-center py-5 ps-8 pe-5 '>
            <div className='d-block'>
              <KTSVG
                className='text-success'
                svgClassName='w-125px h-125px'
                path='/images/icons/duotune/files/fil008.svg'
              />
            </div>
            <div className='d-block text-center mt-6 mb-13'>
              <h1 className='mb-3'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.MODAL.HI'})}
              </h1>
              <p>{intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.MODAL.DESCRIPTION'})}</p>
              <p>{intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.PUBLIC.MODAL.THANKYOU'})}</p>
            </div>
          </div>

          <div className='d-flex align-items-center justify-content-center py-5 ps-8 pe-5 border-top'>
            <div className='d-flex align-items-center me-3'>
              <button className='btn btn-light me-3' onClick={() => setShow(false)}>
                {intl.formatMessage({id: 'BUTTON.CLOSE'})}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {loading && <Loading />}
    </>
  )
}

export default SubcontractorDocumentUpload
